<template>
  <div
    class="d-text-compose"
    :class="{ 'alt-font': altFont }"
  >
    <div ref="textEditor" />
  </div>
</template>

<script>
import Vue from 'vue';

import { debounce } from '@dui/helpers/util-funcs';
import { DEBOUNCE_TIME } from '@d-shared/config';
import { getErrorMessage } from '@d-shared/utils/errors';
import MentionsHelper from '@d-helpers/mentions-helper';
import mentionsService from '@d-services/mentions-service/mentions-service';
import Quill from '@dui/quill/quill';

function _getContentsWithMention(textContent, mention) {
  const newContents = [];

  const splitContents = textContent.split(MentionsHelper.getMentionPlaceholder(mention.id));
  const splitContentsMaxIdx = (splitContents.length - 1);

  splitContents.forEach((text, idx) => {
    newContents.push({ insert: text });

    if (idx === splitContentsMaxIdx) {
      return;
    }

    newContents.push({
      insert: {
        mention: {
          id: mention.id,
          username: mention.label,
        },
      },
    });
  });

  return newContents;
}

function _getEditorContentsFromValue(value, mentions) {
  if (!value) {
    return [
      {
        insert: '\n',
      },
    ];
  }

  let contents = [
    {
      insert: value,
    },
  ];

  if (!mentions) {
    return contents;
  }

  mentions.forEach((mention) => {
    let updatedContents = [];

    contents.forEach((c) => {
      if (typeof c.insert !== 'string') {
        updatedContents.push(c);
        return;
      }

      const splitContents = _getContentsWithMention(c.insert, mention);
      updatedContents = updatedContents.concat(splitContents);
    });

    contents = updatedContents;
  });

  contents.push({ insert: '\n' });

  return contents;
}

export default {
  props: {
    altFont: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    mentions: {
      type: Array,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Start writing here...',
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    _boundFns: null,
    editor: null,
  }),
  beforeDestroy() {
    this.destroyMentionModule();
    this.removeEventListeners();
  },
  mounted() {
    this.initBoundFns();
    this.initQuill();
    this.setInitialContent();
    this.addEventListeners();
    this.setAutoFocus();

    this.destroyMentionModule();
  },
  methods: {
    clear() {
      this.editor.setContents([
        {
          insert: '\n',
        },
      ]);
    },

    debounceSearchMentions: debounce(function(searchTerm, page, renderList) {
      this.searchMentions(searchTerm, page, renderList);
    }, DEBOUNCE_TIME.fast),

    destroyMentionModule() {
      this.editor.getModule('mention').destroy();
    },

    initBoundFns() {
      this._boundFns = {
        enterPress: this.enterPressPerformed.bind(this),
        selectionChange: this.selectionChangePerformed.bind(this),
        textChange: this.textChangePerformed.bind(this),
      };
    },

    initQuill() {
      let options = {
        modules: {
          keyboard: {
            bindings: {
              sendMac: {
                key: 'Enter',
                metaKey: true,
                handler: this._boundFns.enterPress,
              },
              sendWindows: {
                key: 'Enter',
                ctrlKey: true,
                handler: this._boundFns.enterPress,
              },
            },
          },
          mention: {
            allowedChars: /^[a-zA-Z0-9\ \-'\u00C0-\u00FF]*$/,
            minChars: 2,
            source: this.debounceSearchMentions,
          },
          toolbar: false,
        },
        formats: [ 'mention' ],
      };

      options.placeholder = this.placeholder;

      this.editor = new Quill(this.$refs.textEditor, options);
    },

    focus() {
      this.editor.setSelection(this.editor.getLength());
    },

    getEditorContent() {
      const content = this.editor.getContents();
      if (content.ops[content.ops.length - 1].insert === '\n') {
        content.ops.pop();
      }

      if (content.ops.length === 0) {
        return null;
      }

      const lastOp = content.ops[content.ops.length - 1];
      if ((typeof lastOp.insert === 'string') && lastOp.insert.endsWith('\n')) {
        lastOp.insert = lastOp.insert.slice(0, -1);
      }

      return content.ops.filter((op) =>{
        return (typeof op.insert === 'string' ||
          !!op.insert.mention);
      }).map((op) => {
        if (!!op.insert.mention) {
          return MentionsHelper.getMentionPlaceholder(op.insert.mention.id);
        }

        return op.insert;
      }).join('');
    },

    getMentions() {
      return this.editor.getContents().ops
        .filter((op) => !!op.insert.mention)
        .map((op) => {
          return op.insert.mention.id;
        })
        .filter((entry, idx, self) => self.indexOf(entry) === idx);
    },

    async searchMentions(searchTerm, page, renderList) {
      try {
        const results = await mentionsService.search(searchTerm, page);
        renderList(results.users, results.meta);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
        renderList([], null);
      }
    },

    async setAutoFocus() {
      if (!this.autoFocus) {
        return;
      }

      await this.$nextTick();
      this.focus();
    },

    setInitialContent() {
      this.setEditorContentFromText(this.value);
    },

    setEditorContentFromText(textContent) {
      const contents = _getEditorContentsFromValue(textContent, this.mentions);
      this.editor.setContents(contents, Quill.sources.USER);
    },

    // Events
    addEventListeners() {
      this.editor.on('selection-change', this._boundFns.selectionChange);
      this.editor.on('text-change', this._boundFns.textChange);
    },

    removeEventListeners() {
      this.editor.off('selection-change', this._boundFns.selectionChange);
      this.editor.off('text-change', this._boundFns.textChange);
    },

    enterPressPerformed() {
      this.$emit('meta-enter');
    },

    selectionChangePerformed(range) {
      if (range) {
        this.$emit('focus');
        return;
      }

      this.$emit('blur');
    },

    textChangePerformed() {
      const content = this.getEditorContent();
      this.$emit('input', content);
    },
    // END Events
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

$mention-list-container-min-width: 170px;

.d-text-compose {
  background-color: getColor($white);

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .ql-container {
    border: none;
    font-size: $font-size-base;

    .ql-editor {
      padding: 0;

      &:before {
        color: getColor($grey, dark);
        font-style: normal;
        left: 0;
        right: initial;
      }
    }

    .ql-mention-list-container {
      background-color: getColor($white);
      border: $border-width-base solid getColor($grey, fade);
      border-radius: $border-radius-base;
      box-shadow: $box-shadow-context-menu;
      max-height: 250px;
      min-width: $mention-list-container-min-width;
      overflow: auto;
      z-index: 1;

      @include scrollBar();

      &.loading {
        .loading-indicator {
          display: block;
        }
      }

      .loading-indicator {
        display: none;
        padding: $_1em 0;
        text-align: center;
        width: 100%;
      }

      .ql-mention-list {
        list-style: none;
        margin: 0;
        min-width: ($mention-list-container-min-width - (2 * $border-width-base));
        padding: 0;

        .ql-mention-list-item {
          align-items: stretch;
          cursor: pointer;
          display: flex;
          padding: $_1em $_2em $_1em $_1-5em;

          &.selected {
            background-color: getColor($grey);
          }

          img {
            border-radius: 50%;
            height: $_4em;
            margin-right: $_1-5em;
            width: $_4em;
          }

          span {
            align-items: baseline;
            display: flex;
            flex: none;
            flex-direction: column;
            justify-content: space-between;
            line-height: 1;
          }
        }
      }
    }
  }

  &.alt-font .ql-container {
    font-family: $alt-font;
  }

  .ql-mention {
    background-color: transparent;
    color: getColor($yellow);
    font-weight: bold;
    padding: 0;

    > span {
      margin: 0;
    }

    a {
      color: getColor($yellow);
    }
  }
}
</style>
